import { z } from "zod";
import { EXPLORER_API_ERRORS, type ExplorerApiErrorCode } from "./errors.ts";
import { CONSOLE_CSRF_COOKIE, CONSOLE_SESSION_COOKIE, LOGIN_STEP_COOKIE } from "./auth.ts";
import {
  EXPLORER_API_ROUTES,
  type ExplorerApiModule,
  type ModuleExplorerApiRoute,
} from "./api-routes.ts";
import { DOCS_ACCESS_COOKIES } from "./docs-access.ts";
import { EXPLORER_API_BASE, type ExplorerApiRoute } from "./routes.ts";

// OpenAPI 3.1 document of the Explorer API (`openapi/explorer-v1.json`), a separate document from
// the Token/Verify one: generated from the zod schemas and the route table, never hand-edited.

type Json = Record<string, unknown>;

const ref = (id: string): Json => ({ $ref: `#/components/schemas/${id}` });

/** Inline JSON Schema of a request-side schema (query parameter or body), as the client sends it. */
function inputSchema(schema: z.ZodType): Json {
  const json = z.toJSONSchema(schema, {
    target: "draft-2020-12",
    io: "input",
    unrepresentable: "any",
    metadata: z.registry(),
  }) as Json;
  delete json.$schema;
  return json;
}

function componentId(schema: z.ZodType): string {
  const id = z.globalRegistry.get(schema)?.id;
  if (id === undefined) throw new Error("Explorer response schemas must declare a meta id");
  return id;
}

function errorResponses(codes: readonly ExplorerApiErrorCode[]): Json {
  const byStatus = new Map<number, ExplorerApiErrorCode[]>();
  for (const code of codes) {
    const status = EXPLORER_API_ERRORS[code].status;
    byStatus.set(status, [...(byStatus.get(status) ?? []), code]);
  }
  return Object.fromEntries(
    [...byStatus]
      .sort(([a], [b]) => a - b)
      .map(([status, list]) => [
        String(status),
        {
          description: list.map((c) => `\`${c}\`: ${EXPLORER_API_ERRORS[c].description}`).join(" "),
          content: { "application/json": { schema: ref("ExplorerApiError") } },
        },
      ]),
  );
}

function parameters(route: ExplorerApiRoute): Json[] {
  const out: Json[] = [];
  const add = (location: "path" | "query", object: z.ZodObject): void => {
    const required = new Set((inputSchema(object).required as string[] | undefined) ?? []);
    for (const [name, field] of Object.entries(object.shape)) {
      out.push({
        name,
        in: location,
        required: location === "path" || required.has(name),
        ...(location === "query" ? { style: "form", explode: true } : {}),
        schema: inputSchema(field as z.ZodType),
      });
    }
  };
  if (route.params !== undefined) add("path", route.params);
  if (route.query instanceof z.ZodObject) add("query", route.query);
  if (route.cacheable === true)
    out.push({
      name: "If-None-Match",
      in: "header",
      required: false,
      description: "The ETag of a cached answer; when it still matches, the answer is 304.",
      schema: { type: "string" },
    });
  return out;
}

function successDescription(route: ExplorerApiRoute): string {
  if (route.successDescription !== undefined) return route.successDescription;
  if (route.successStatus === 202) return "Accepted: generated in the background.";
  if (route.successStatus === 302) return "Redirect to the file.";
  if (route.successStatus === 303) return "Redirect (no body).";
  if (route.successStatus === 204) return "Done (no body).";
  if (route.alternativeSuccess !== undefined) return "Updated.";
  return "OK";
}

const LOGIN_STEP_COOKIE_DESCRIPTION = `The login-step cookie ${LOGIN_STEP_COOKIE} (HttpOnly; Secure; SameSite=Strict; Path=/; 3 minutes for the code, 10 minutes for the set-up) that binds the next step to this browser. No session cookie is set.`;

const SET_COOKIE: Record<NonNullable<ExplorerApiRoute["setsCookies"]>, string> = {
  console_session: `The console session cookie ${CONSOLE_SESSION_COOKIE} (HttpOnly; Secure; SameSite=Strict; Path=/) and the synchronizer token cookie ${CONSOLE_CSRF_COOKIE} (Secure; SameSite=Strict; Path=/; readable by the SPA), both until the session's absolute expiry.`,
  console_session_cleared: `Clears ${CONSOLE_SESSION_COOKIE} and ${CONSOLE_CSRF_COOKIE} (Max-Age=0).`,
  docs_access: `The CloudFront signed cookies of the integration docs site (${DOCS_ACCESS_COOKIES.join(", ")}; HttpOnly; Secure; SameSite=Strict) until expires_at.`,
};

function successHeaders(route: ExplorerApiRoute): Json | undefined {
  const headers: Json = {};
  if (route.successStatus === 302)
    headers.Location = {
      description: "The presigned URL of the file.",
      schema: { type: "string", format: "uri" },
    };
  if (route.successStatus === 303)
    headers.Location = {
      description: route.redirect ?? "Where the browser goes next.",
      schema: { type: "string" },
    };
  if (route.cacheable === true) {
    headers["Cache-Control"] = {
      description: "private, max-age=86400 (reference data; revalidate with the ETag).",
      schema: { type: "string" },
    };
    headers.ETag = {
      description: "Changes only when the answer changes (a new dataset version).",
      schema: { type: "string" },
    };
  }
  if (route.publicMaxAgeSeconds !== undefined)
    headers["Cache-Control"] = {
      description: `public, max-age=${String(route.publicMaxAgeSeconds)} (installation configuration; no secret).`,
      schema: { type: "string" },
    };
  if (route.setsCookies !== undefined)
    headers["Set-Cookie"] = {
      description: SET_COOKIE[route.setsCookies],
      schema: { type: "array", items: { type: "string" } },
    };
  return Object.keys(headers).length === 0 ? undefined : headers;
}

function successResponse(route: ExplorerApiRoute, description: string): Json {
  const headers = successHeaders(route);
  return {
    description,
    ...(headers === undefined ? {} : { headers }),
    ...(route.response === undefined
      ? {}
      : { content: { "application/json": { schema: ref(componentId(route.response)) } } }),
  };
}

/** Public routes need nothing; the others the session cookie, plus X-CSRF-Token when unsafe. */
function security(route: ExplorerApiRoute): Json[] {
  if (route.needsLoginStep === true) return [{ loginStep: [] }];
  if (route.permission === "public") return [];
  return route.method === "GET"
    ? [{ consoleSession: [] }]
    : [{ consoleSession: [], csrfToken: [] }];
}

function operation(route: ModuleExplorerApiRoute): Json {
  const params = parameters(route);
  return {
    operationId: route.operationId,
    tags: [route.module],
    summary: route.summary,
    ...(route.description === undefined ? {} : { description: route.description }),
    security: security(route),
    "x-strust-module": route.module,
    "x-strust-permission": route.permission,
    ...(route.preciseLocation === true
      ? { "x-strust-precise-location": "location.precise.read" }
      : {}),
    ...(params.length === 0 ? {} : { parameters: params }),
    ...(route.body === undefined
      ? {}
      : {
          requestBody: {
            required: true,
            content: { "application/json": { schema: inputSchema(route.body) } },
          },
        }),
    responses: {
      ...(route.csv === true
        ? {
            "200": {
              description: "The CSV file, answered at once (attachment).",
              content: { "text/csv": { schema: { type: "string" } } },
            },
          }
        : {}),
      [String(route.successStatus)]: successResponse(route, successDescription(route)),
      ...(route.cacheable === true
        ? { "304": { description: "Not modified: If-None-Match matched the ETag (no body)." } }
        : {}),
      ...(route.alternativeSuccess === undefined
        ? {}
        : {
            [String(route.alternativeSuccess.status)]: successResponse(
              route,
              route.alternativeSuccess.description,
            ),
          }),
      ...(route.loginStep === undefined
        ? {}
        : {
            "202": {
              description: route.loginStep.description,
              headers: {
                "Set-Cookie": {
                  description: LOGIN_STEP_COOKIE_DESCRIPTION,
                  schema: { type: "array", items: { type: "string" } },
                },
              },
              content: {
                "application/json": { schema: ref(componentId(route.loginStep.response)) },
              },
            },
          }),
      ...errorResponses(route.errors),
    },
  };
}

const MODULE_TAGS: Record<ExplorerApiModule, string> = {
  explorer:
    "Investigation, blocklist, notes, exports, audit, preferences and notifications (module explorer).",
  iam: "Authentication, invitations, the session's console user, console users, the organization identity provider, docs access and the platform organizations and invitations (module iam).",
  "operator-auth":
    "Platform area: operators, their environment, products and API credentials (module operator-auth).",
  fleet:
    "Fleet tab of the platform installation: customer installations, releases, rollouts, customers and the console audit trail (module fleet; release flag fleet.console).",
};

/** Every `$ref` id reachable from `node`. */
function refsOf(node: unknown, into: Set<string>): Set<string> {
  if (Array.isArray(node)) for (const item of node) refsOf(item, into);
  else if (typeof node === "object" && node !== null)
    for (const [key, value] of Object.entries(node)) {
      if (key === "$ref" && typeof value === "string") {
        const id = value.replace("#/components/schemas/", "");
        into.add(id);
      } else refsOf(value, into);
    }
  return into;
}

/** OpenAPI 3.1 document of `/explorer/v1` (deterministic: generated from the route table). */
export function buildExplorerOpenApiDocument(): Json {
  const paths: Record<string, Json> = {};
  for (const route of EXPLORER_API_ROUTES) {
    const path = `${EXPLORER_API_BASE}${route.path}`;
    const method = route.method.toLowerCase();
    if (paths[path]?.[method] !== undefined)
      throw new Error(`Duplicate Explorer route ${route.method} ${path}`);
    paths[path] = { ...paths[path], [method]: operation(route) };
  }
  const { schemas: all } = z.toJSONSchema(z.globalRegistry, {
    target: "draft-2020-12",
    uri: (id) => `#/components/schemas/${id}`,
    unrepresentable: "any",
  }) as { schemas: Record<string, Json> };
  // Only the components the Explorer paths reach (the registry also holds the Token/Verify schemas).
  const needed = refsOf(paths, new Set(["ExplorerApiError"]));
  let size = 0;
  while (needed.size !== size) {
    size = needed.size;
    for (const id of [...needed]) refsOf(all[id], needed);
  }
  const schemas = Object.fromEntries(
    [...needed].sort().map((id) => {
      const schema = { ...all[id] };
      delete schema.$id;
      delete schema.$schema;
      return [id, schema];
    }),
  );
  return {
    openapi: "3.1.0",
    info: {
      title: "S-Trust Explorer API",
      version: "2.0.0",
      description:
        "Console API of the Explorer (role explorer, same origin as the SPA): every /explorer/v1 route, whichever module serves it (x-strust-module). Console-session cookie; unsafe methods also require X-CSRF-Token. Every route declares who may call it (x-strust-permission: a permission code, authenticated = any live console session, public = no session, rate limited per client IP); a resource of another operator answers 404, and so does a platform route to a caller without the platform permission. Generated from packages/contracts.",
    },
    servers: [{ url: "/" }],
    tags: (Object.keys(MODULE_TAGS) as ExplorerApiModule[]).map((name) => ({
      name,
      description: MODULE_TAGS[name],
    })),
    components: {
      schemas,
      securitySchemes: {
        consoleSession: {
          type: "apiKey",
          in: "cookie",
          name: CONSOLE_SESSION_COOKIE,
          description:
            "HttpOnly; Secure; SameSite=Strict console session cookie set by iam (login, invitation acceptance, SSO callback); never readable by JavaScript. Sessions end after 30 min idle or 12 h.",
        },
        loginStep: {
          type: "apiKey",
          in: "cookie",
          name: LOGIN_STEP_COOKIE,
          description:
            "HttpOnly; Secure; SameSite=Strict login-step cookie set with a 202 login answer (mfa_required, mfa_setup_required or new_password_required): it binds the code step (3 min, 3 codes), the mandatory set-up (10 min) or the new password of a temporary one (3 min, 3 attempts) to the browser that entered the password. Without it these routes answer mfa_challenge_expired.",
        },
        csrfToken: {
          type: "apiKey",
          in: "header",
          name: "X-CSRF-Token",
          description: `Synchronizer token of the console session, required on every unsafe method (POST, PUT, PATCH). It is returned as csrf_token when the session starts and kept in the JS-readable cookie ${CONSOLE_CSRF_COOKIE} (Secure; SameSite=Strict) for reloads and SSO redirects; a missing or wrong value answers 403 csrf_token_invalid.`,
        },
      },
    },
    paths,
  };
}
